import { useEffect, useState } from 'react';
import { btnStyle, innerFormContainerStyle, outerFormContainerStyle, rightHeaderContainerStyle } from '@vivli/shared/components';
import { useActiveUserContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { ApproveRejectButtonsComponent } from '../approve-reject-buttons.component';
import { CancelButtonView } from '../cancel-button.component';
import { EditDataRequestButtonView } from '../edit-data-request-button.component';
import { ResetToDraftButtonView } from '../reset-to-draft-button.component';
import { SetTrackingButtonView } from '../set-tracking-button.component';
import { ShowApprovalButtonsComponent } from '../show-approval-buttons.component';
import { LeftHeaderComponent } from '../left-header.component';
import { Size } from '@vivli/shared/theme';
import { PrintButtonContainerComponent } from '../print-button-container.component';
import { useDataRequestAdminPath } from '@vivli/features/data-requests/infrastructure/hook';
import { useLocation } from 'react-router';
import { DTICommonConst } from '@vivli/shared/infrastructure/constants';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { ButtonView, SubmitButtonView } from '@vivli/shared/views';
import { useFormState } from 'react-hook-form';

interface RequestHeaderComponentProps {
    onEditTitleClick?: () => void;
    onSaveClick?: () => void;
    onSubmitClick?: () => void;
    title?: string;
    showTrackingButton: boolean;
    showResetToDraftButton: boolean;
    showCancelButton: boolean;
    showEditDataRequestButton: boolean;
    showSaveButtons: boolean;
    showSubmitButton?: boolean;
    showEditTitleButton: boolean;
    showPrintButton: boolean;
    myDataRequests?: boolean;
    showArchiveButton: boolean;
    onClickArchiveButton?: () => void;
    style?: React.CSSProperties;
}

export const RequestHeaderComponent = ({
    onEditTitleClick,
    onSaveClick,
    onSubmitClick,
    title,
    showTrackingButton,
    showResetToDraftButton,
    showCancelButton,
    showEditDataRequestButton,
    showSaveButtons,
    showSubmitButton = true,
    showEditTitleButton,
    showPrintButton,
    myDataRequests,
    showArchiveButton,
    onClickArchiveButton,
    style
}: RequestHeaderComponentProps) => {
    const { hasAnyPermission, hasPermission } = useActiveUserContext();
    const {
        dataRequest,
        showApprovalButtons,
        isAdminEditing,
        setIsAdminEditing,
        isSavingDataRequest,
        isSubmittingDataRequest,
        isUpdatingStatus,
        setIsUpdatingStatus,
        updateDataRequest,
        includeRiskScore,
        setIncludeRiskScore,
        surveyHasErrors,
        isFormDirty
    } = useDataRequestContext();
    const [showIRPOverride, setShowIRPOverride] = useState<boolean>(false);
    const [showDataProviderOverride, setShowDataProviderOverride] = useState<boolean>(false);
    const [showRiskScoreButton, setShowRiskScoreButton] = useState<boolean>(false);
    const isDataRequestsView = useDataRequestAdminPath();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);

    const { isValid } = useFormState();

    //TODO PERMFE - redo with permissions+
    const isOrgAdmin = !isVivliAdmin && hasAnyPermission([PermissionsEnum.ProviderActAdv]);
    const { isAmr } = useAssignedAppTypeContext();

    // Location hook used to read the params passed from the history hook, if any
    // and THEN setting the property in the context. Setting the property
    // in the context directly was a problem.

    const location = useLocation();
    const path = location.pathname.toLowerCase();
    const notSubmittable = dataRequest.status !== DataRequestStatusEnum.Draft;

    const checkLastTerm = (urlPath) => {
        const segments = urlPath.split('/').filter((segment) => segment !== '');
        const lastSegment = segments[segments.length - 1];

        if (lastSegment === 'requestdetailsprintview') {
            setShowRiskScoreButton(true);
        } else {
            setIncludeRiskScore(false);
            setShowRiskScoreButton(false);
        }
    };

    useEffect(() => {
        const vivliAdminRequestedEdit = location?.state?.detail === true;
        if (vivliAdminRequestedEdit) {
            setIsAdminEditing(true);
        }
    }, []);

    //Need to hide certain buttons while in AMR if admin is editing
    const hideButtons = isAdminEditing || !isDataRequestsView;
    useEffect(() => {
        //TODO PERMFE - redo with permissions+
        setShowIRPOverride(
            dataRequest.status === DataRequestStatusEnum.AwaitingIRPApproval && isVivliAdmin && !showApprovalButtons && !isAdminEditing
        );
        setShowDataProviderOverride(
            dataRequest.status === DataRequestStatusEnum.AwaitingDataProviderApproval &&
                isVivliAdmin &&
                !showApprovalButtons &&
                !isAdminEditing
        );
    }, [dataRequest.status, showApprovalButtons, isAdminEditing]);

    useEffect(() => {
        checkLastTerm(path);
    }, [path]);

    return (
        <div style={outerFormContainerStyle} className="do-not-print">
            <div style={innerFormContainerStyle}>
                <LeftHeaderComponent
                    title={title}
                    onEditTitleClick={onEditTitleClick}
                    showEditTitleButton={isAmr ? showEditTitleButton : false}
                    myDataRequests={myDataRequests}
                    isAmr={isAmr}
                />

                <div style={rightHeaderContainerStyle}>
                    {(isVivliAdmin || isOrgAdmin) && showRiskScoreButton && (
                        <ButtonView
                            title={includeRiskScore ? 'Include Risk Score' : 'Remove Risk Score'}
                            style={{
                                ...style,
                                marginLeft: Size.PADDING,
                                marginRight: Size.PADDING
                            }}
                            onClick={() => setIncludeRiskScore(!includeRiskScore)}
                        >
                            {includeRiskScore ? 'Remove Risk Score' : 'Include Risk Score'}
                        </ButtonView>
                    )}

                    {!isOrgAdmin && (
                        <>
                            {showArchiveButton && onClickArchiveButton && (
                                <ButtonView
                                    dataId={DTICommonConst.ArchiveButton}
                                    title="Archive"
                                    style={{
                                        ...style,
                                        marginLeft: Size.PADDING,
                                        marginRight: Size.PADDING
                                    }}
                                    onClick={onClickArchiveButton}
                                >
                                    Archive
                                </ButtonView>
                            )}
                            {showTrackingButton && <SetTrackingButtonView />}

                            {showResetToDraftButton && <ResetToDraftButtonView dataId={DTICommonConst.DraftButton} />}

                            {showCancelButton && (
                                <CancelButtonView
                                    dataId={DTICommonConst.CancelButton}
                                    setIsUpdatingStatus={setIsUpdatingStatus}
                                    isDisabled={isUpdatingStatus}
                                    dataRequest={dataRequest}
                                    updateDataRequest={updateDataRequest}
                                />
                            )}

                            {showEditDataRequestButton && <EditDataRequestButtonView />}
                        </>
                    )}

                    {showIRPOverride && (
                        <ShowApprovalButtonsComponent
                            message={
                                'Proceeding will allow you to take actions on behalf of an IRP/Approver or Data Contributor.  Proceed?'
                            }
                            title={'IRP Admin Override'}
                        />
                    )}

                    {showDataProviderOverride && (
                        <ShowApprovalButtonsComponent
                            message={'Proceeding will allow you to take actions on behalf of a Data Provider.  Proceed?'}
                            title={'Data Provider Override'}
                        />
                    )}

                    {showApprovalButtons && !hideButtons && (
                        <span style={{ marginLeft: 10 }}>
                            <ApproveRejectButtonsComponent />
                        </span>
                    )}

                    {showSaveButtons && (
                        <>
                            <ButtonView
                                title="Save"
                                style={{
                                    ...btnStyle,
                                    marginLeft: Size.PADDING
                                }}
                                disabled={!isFormDirty || isSavingDataRequest}
                                isLoading={isSavingDataRequest}
                                onClick={onSaveClick}
                                dataId={DTICommonConst.SaveButton}
                            >
                                Save
                            </ButtonView>

                            {/* DEV-586: hidden for a contributing researcher (has DR access, not Lead/Request Admin) */}
                            {showSubmitButton && (
                                <SubmitButtonView
                                    // style={submitBtnStyle}
                                    disabled={!isValid || isSubmittingDataRequest || notSubmittable || surveyHasErrors}
                                    isLoading={isSubmittingDataRequest}
                                    onClick={onSubmitClick}
                                    dataId={DTICommonConst.SubmitButton}
                                >
                                    Submit
                                </SubmitButtonView>
                            )}
                        </>
                    )}
                    {showPrintButton && <PrintButtonContainerComponent />}
                </div>
            </div>
        </div>
    );
};
